(() => {
  const SIZE = 800;
  const DEFAULT_THICKNESS = 1.5;
  const SCALE_MIN = 0.5;
  const SHADOW_PADDING = 24;
  const FORCE_THICK_OVER_WIDTH = 24;
  const FORCED_THICKNESS = 2;

  function getConfiguratorData() {
    const dataElement = document.getElementById('print-configurator-data');

    if (!dataElement) {
      return { frameOption: {}, matOption: {}, productImage: '', interiorImage: '', sizeOptionName: '', availableSizes: [] };
    }

    return JSON.parse(dataElement.textContent);
  }

  const imageCache = new Map();

  let latestRenderId = 0;

  function loadImage(url) {
    if (!url) {
      return Promise.resolve(null);
    }

    if (!imageCache.has(url)) {
      imageCache.set(url, new Promise((resolve) => {
        const img = new Image();

        img.onload = () => resolve(img);
        img.onerror = () => {
          imageCache.delete(url);
          resolve(null);
        };

        img.src = url;
      }));
    }

    return imageCache.get(url);
  }

  function copyCanvas(source, target) {
    target.width = source.width;
    target.height = source.height;
    target.getContext('2d').drawImage(source, 0, 0);
  }

  function parseDimensions(sizeValue) {
    if (!sizeValue) {
      return null;
    }

    const match = sizeValue.match(/(\d+(?:\.\d+)?)\s*[x×]\s*(\d+(?:\.\d+)?)/i);

    if (!match) {
      return null;
    }

    return { width: parseFloat(match[1]), height: parseFloat(match[2]) };
  }

  function getMaxDimension(availableSizes) {
    let max = 0;

    for (const size of availableSizes) {
      const dimensions = parseDimensions(size);

      if (!dimensions) {
        continue;
      }

      const larger = Math.max(dimensions.width, dimensions.height);

      if (larger > max) {
        max = larger;
      }
    }

    return max;
  }

  function getSizeScale(sizeValue, maxDimension) {
    if (!sizeValue || !maxDimension) {
      return 1;
    }

    const dimensions = parseDimensions(sizeValue);

    if (!dimensions) {
      return 1;
    }

    const ratio = Math.max(dimensions.width, dimensions.height) / maxDimension;

    return SCALE_MIN + ratio * (1 - SCALE_MIN);
  }

  function getMatInset(sizeValue, matThickness) {
    if (!sizeValue) {
      return null;
    }

    const dimensions = parseDimensions(sizeValue);

    if (!dimensions) {
      return null;
    }

    let matInches = matThickness;

    if (!matInches) {
      const larger = Math.max(dimensions.width, dimensions.height);

      matInches = larger >= 36 ? 3 : 2;
    }

    return {
      x: matInches / dimensions.width,
      y: matInches / dimensions.height
    };
  }

  async function renderPrint(canvas, { productImage, frameStyle, matStyle, sizeValue, scale }) {
    canvas.width = SIZE;
    canvas.height = SIZE;
    const ctx = canvas.getContext('2d');

    ctx.clearRect(0, 0, SIZE, SIZE);

    const printSize = Math.round((SIZE - SHADOW_PADDING * 2) * scale);

    const dimensions = parseDimensions(sizeValue);
    const aspect = dimensions ? dimensions.width / dimensions.height : 1;
    const printWidth = aspect >= 1 ? printSize : Math.round(printSize * aspect);
    const printHeight = aspect >= 1 ? Math.round(printSize / aspect) : printSize;

    let thicknessInches = frameStyle ? (frameStyle.thickness ?? DEFAULT_THICKNESS) : 0;

    if (frameStyle && dimensions && dimensions.width > FORCE_THICK_OVER_WIDTH) {
      thicknessInches = Math.max(thicknessInches, FORCED_THICKNESS);
    }

    const pixelsPerInch = dimensions ? printWidth / dimensions.width : 0;
    const frameInset = Math.round(thicknessInches * pixelsPerInch);
    const frameInsetX = frameInset;
    const frameInsetY = frameInset;
    const matRatios = matStyle ? getMatInset(sizeValue, matStyle.thickness) : null;
    const matInsetX = matRatios ? Math.round(printWidth * matRatios.x) : 0;
    const matInsetY = matRatios ? Math.round(printHeight * matRatios.y) : 0;
    const imageInsetX = frameInsetX + matInsetX;
    const imageInsetY = frameInsetY + matInsetY;

    const productImg = await loadImage(productImage);

    const hasFrameImage = frameStyle?.images && Object.values(frameStyle.images).some(Boolean);

    const printX = Math.round((SIZE - printWidth) / 2);
    const printY = Math.round((SIZE - printHeight) / 2);

    let drawX, drawY, drawWidth, drawHeight;

    if (productImg) {
      const imageWidth = productImg.naturalWidth;
      const imageHeight = productImg.naturalHeight;
      const availableWidth = printWidth - imageInsetX * 2;
      const availableHeight = printHeight - imageInsetY * 2;
      const imgScale = Math.min(availableWidth / imageWidth, availableHeight / imageHeight);
      drawWidth = Math.round(imageWidth * imgScale);
      drawHeight = Math.round(imageHeight * imgScale);
      drawX = Math.round((SIZE - drawWidth) / 2);
      drawY = Math.round((SIZE - drawHeight) / 2);
    } else {
      drawWidth = printWidth - imageInsetX * 2;
      drawHeight = printHeight - imageInsetY * 2;
      drawX = Math.round((SIZE - drawWidth) / 2);
      drawY = Math.round((SIZE - drawHeight) / 2);
    }

    const totalWidth = drawWidth + imageInsetX * 2;
    const totalHeight = drawHeight + imageInsetY * 2;
    const totalX = drawX - imageInsetX;
    const totalY = drawY - imageInsetY;

    ctx.save();
    ctx.shadowColor = 'rgba(0, 0, 0, 0.18)';
    ctx.shadowBlur = Math.round(printSize * 0.04);
    ctx.shadowOffsetX = 0;
    ctx.shadowOffsetY = Math.round(printSize * 0.02);
    ctx.fillStyle = '#ffffff';
    ctx.fillRect(totalX, totalY, totalWidth, totalHeight);
    ctx.restore();

    if (matStyle) {
      ctx.fillStyle = matStyle.color;
      ctx.fillRect(totalX, totalY, totalWidth, totalHeight);
    }

    if (productImg) {
      ctx.drawImage(productImg, drawX, drawY, drawWidth, drawHeight);
    } else {
      ctx.fillStyle = '#e0e0e0';
      ctx.fillRect(drawX, drawY, drawWidth, drawHeight);
    }

    if (frameStyle) {
      if (hasFrameImage) {
        const targetAspect = totalWidth / totalHeight;
        const imageEntries = Object.values(frameStyle.images).filter(Boolean);
        const loadedFrameImgs = await Promise.all(imageEntries.map((url) => loadImage(url)));
        const validFrameImgs = loadedFrameImgs.filter(Boolean);

        if (validFrameImgs.length > 0) {
          validFrameImgs.sort((a, b) => {
            const aspectA = a.naturalWidth / a.naturalHeight;
            const aspectB = b.naturalWidth / b.naturalHeight;

            return Math.abs(aspectA - targetAspect) - Math.abs(aspectB - targetAspect);
          });

          ctx.drawImage(validFrameImgs[0], totalX, totalY, totalWidth, totalHeight);
        }
      } else if (frameStyle.color) {
        ctx.fillStyle = frameStyle.color;
        ctx.fillRect(totalX, totalY, totalWidth, frameInset);
        ctx.fillRect(totalX, totalY + totalHeight - frameInset, totalWidth, frameInset);
        ctx.fillRect(totalX, totalY + frameInset, frameInset, totalHeight - frameInset * 2);
        ctx.fillRect(totalX + totalWidth - frameInset, totalY + frameInset, frameInset, totalHeight - frameInset * 2);
      }
    }

    canvas.dataset.printOffsetY = totalY;

    return canvas;
  }

  async function renderInterior(canvas, { interiorImage, printCanvas, scale, interiorPrintWidth, interiorPrintTop }) {
    const interiorImg = await loadImage(interiorImage);

    if (!interiorImg) {
      return;
    }

    const interiorWidth = interiorImg.naturalWidth;
    const interiorHeight = interiorImg.naturalHeight;

    canvas.width = interiorWidth;
    canvas.height = interiorHeight;

    const ctx = canvas.getContext('2d');

    ctx.drawImage(interiorImg, 0, 0, interiorWidth, interiorHeight);

    const maxPrintWidth = Math.round(interiorWidth * interiorPrintWidth);
    const printWidth = Math.round(maxPrintWidth * scale);
    const printX = Math.round((interiorWidth - printWidth) / 2);
    const printOffsetY = parseFloat(printCanvas.dataset.printOffsetY ?? 0);
    const printOffsetYScaled = Math.round(printOffsetY * (printWidth / SIZE));
    const printY = Math.round(interiorHeight * interiorPrintTop) - printOffsetYScaled;

    ctx.drawImage(printCanvas, printX, printY, printWidth, printWidth);
  }

  function resolveFrameStyle(option, frameValue, colorName, sizeValue) {
    if (!option?.styles || !frameValue) {
      return null;
    }

    if (frameValue.toLowerCase() === 'no') {
      return null;
    }

    const frameKey = frameValue.toLowerCase().trim();
    const frameEntry = option.styles[frameKey] ?? option.styles['default'] ?? null;

    if (!frameEntry) {
      return null;
    }

    const thickness = frameEntry.thickness ?? DEFAULT_THICKNESS;
    const colors = frameEntry.colors ?? {};
    const colorEntry = colors[colorName] ?? colors[Object.keys(colors)[0]] ?? null;

    if (!colorEntry) {
      return { thickness };
    }

    return {
      thickness,
      images: {
        square: colorEntry.imageSquare ?? null,
        landscape: colorEntry.imageLandscape ?? null,
        portrait: colorEntry.imagePortrait ?? null,
      },
      color: colorEntry.color ?? null,
    };
  }

  function resolveMatStyle(option, selectedValue) {
    if (!option?.styles || !option.optionName || !selectedValue) {
      return null;
    }

    if (selectedValue.toLowerCase() === 'no') {
      return null;
    }

    const key = selectedValue.toLowerCase().trim();

    return option.styles[key] ?? option.styles['default'] ?? null;
  }

  async function renderPreview(detail) {
    const renderId = ++latestRenderId;
    const data = getConfiguratorData();
    const optionValues = detail?.optionValues ?? {};

    const productImage = data.productImage || '';
    const interiorImage = data.interiorImage || '';
    const availableSizes = data.availableSizes || [];

    const frameValue = optionValues[data.frameOption?.optionName] ?? '';
    const frameColor = optionValues['_frameColor'] ?? '';
    const matValue = optionValues[data.matOption?.optionName] ?? '';
    const sizeValue = optionValues[data.sizeOptionName] ?? '';

    const frameStyle = resolveFrameStyle(data.frameOption, frameValue, frameColor, sizeValue);
    const matStyle = resolveMatStyle(data.matOption, matValue);

    const maxDimension = getMaxDimension(availableSizes);
    const scale = getSizeScale(sizeValue, maxDimension);

    const printCanvases = document.querySelectorAll('[data-print-canvas], [data-lightbox-print-canvas]');
    const interiorCanvases = document.querySelectorAll('[data-interior-canvas], [data-lightbox-interior-canvas]');

    const interiorPrintWidth = (data.interiorPrintWidth ?? 25) / 100;
    const interiorPrintTop = (data.interiorPrintTop ?? 22) / 100;

    if (!printCanvases.length) {
      return;
    }

    const printCanvas = document.createElement('canvas');
    const interiorCanvas = document.createElement('canvas');

    await renderPrint(printCanvas, { productImage, frameStyle, matStyle, sizeValue, scale });

    if (interiorCanvases.length) {
      await renderInterior(interiorCanvas, { interiorImage, printCanvas, scale, interiorPrintWidth, interiorPrintTop });
    }

    if (renderId !== latestRenderId) {
      return;
    }

    for (const canvas of printCanvases) {
      copyCanvas(printCanvas, canvas);

      canvas.dataset.printOffsetY = printCanvas.dataset.printOffsetY;
    }

    if (interiorCanvas.width && interiorCanvas.height) {
      for (const canvas of interiorCanvases) {
        copyCanvas(interiorCanvas, canvas);
      }
    }
  }

  document.addEventListener('print-configurator:change', (event) => renderPreview(event.detail));
})();
